import { useEffect, useRef, useCallback } from 'react';

/**
 * Reusable "never lose unsaved work" safety net for data-entry forms.
 *
 * It does NOT change a form's existing save logic — it only ADDS a durable
 * client-side backup so work survives session expiry, lost connectivity, tab
 * crashes, or accidental navigation:
 *   • mirrors the form's data object to localStorage on a short debounce
 *   • exposes readBackup() so the form can offer to restore a newer local draft
 *   • warns before unload while there are unsynced changes
 *   • clearBackup() to call after a successful server save
 *
 * Designed for forms that hold their state in a single object (e.g. formData).
 *
 * @param {object}  opts
 * @param {string}  opts.storageKey  unique key per record, e.g. `sv_form_${id}`
 * @param {any}     opts.data        the form's current data object
 * @param {boolean} opts.enabled     true only AFTER the form has loaded cleanly
 * @param {number}  [opts.debounceMs=4000]
 */
export function useFormDraftBackup({ storageKey, data, enabled = true, debounceMs = 4000 }) {
  const timerRef    = useRef(null);
  const dirtyRef    = useRef(false);   // unsynced changes exist?
  const baselineRef = useRef(null);    // serialized data at enable-time / last save

  useEffect(() => {
    if (!enabled || !storageKey) return;
    // Capture a baseline the first time we're enabled so simply loading the
    // form doesn't count as "dirty".
    if (baselineRef.current === null) { baselineRef.current = JSON.stringify(data); return; }

    const cur = JSON.stringify(data);
    if (cur === baselineRef.current) return; // nothing changed since last sync

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify({ data, _savedAt: new Date().toISOString() }));
        dirtyRef.current = true;
      } catch (_) { /* storage full/unavailable */ }
    }, debounceMs);
    return () => clearTimeout(timerRef.current);
  }, [storageKey, data, enabled, debounceMs]);

  // Warn before leaving with unsynced changes.
  useEffect(() => {
    const handler = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Read the saved local draft (call on load to compare against the server copy).
  const readBackup = useCallback(() => {
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  }, [storageKey]);

  // Call after a successful server save so the backup can't trigger a needless
  // restore later, and the unload guard goes quiet.
  const clearBackup = useCallback(() => {
    dirtyRef.current = false;
    baselineRef.current = null; // re-baseline on next render
    if (timerRef.current) clearTimeout(timerRef.current);
    try { if (storageKey) localStorage.removeItem(storageKey); } catch (_) { /* ignore */ }
  }, [storageKey]);

  return { readBackup, clearBackup };
}
